// ============================================================
// assets/july-event-mgr.js
// ============================================================

/**
 * 1. Backend API Client
 */
class EventApiClient {
    constructor(customerId, eventId) {
        this.baseUrl = (window.routes && window.routes.api_url) ? window.routes.api_url : '';
        this.customerId = String(customerId);
        this.eventId = eventId;
    }

    async _fetch(endpoint, options = {}) {
        const url = `${this.baseUrl}${endpoint}`;
        const config = {
            mode: 'cors',
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...options.headers
            }
        };

        try {
            const res = await fetch(url, config);
            const json = await res.json();
            if (!res.ok) throw new Error(json.message || `API Error ${res.status}`);
            return json;
        } catch (err) {
            console.error(`[API Fail] ${endpoint}:`, err);
            throw err;
        }
    }

    async checkIn() {
        return this._fetch('/event/check-in', {
            method: 'POST',
            body: JSON.stringify({
                customerId: this.customerId,
                eventId: this.eventId,
                missionId: 'mission_attendance'
            })
        });
    }

    async exchangeReward(rewardId) {
        return this._fetch('/event/reward-exchange', {
            method: 'POST',
            body: JSON.stringify({
                customerId: this.customerId,
                eventId: this.eventId,
                rewardId: rewardId
            })
        });
    }

    async getHistory(limit = 1, lastKey = null) {
        const params = new URLSearchParams({
            customerId: this.customerId,
            eventId: this.eventId,
            limit: limit
        });
        if (lastKey) params.append('lastKey', lastKey);
        return this._fetch(`/event/history?${params.toString()}`, { method: 'GET' });
    }
}

/**
 * 2. Event State Manager
 */
class EventStateManager {
    constructor() {
        this.state = {
            isLoggedIn: false,
            config: {
                settings: {},
                missions: [],
                rewards: [],
                gacha: {}
            },
            user: {
                id: null,
                currentStamps: 0,
                claimedRewards: [],
                attendanceHistory: [],
                missionCounts: {
                    attendance: 0,
                    order: 0,
                    review: 0,
                    bonusAll: 0,
                    bonusOrder: 0,
                    signup: 0,
                    luckyDrop: 0
                }
            }
        };
    }

    _parseJson(elementId) {
        const el = document.getElementById(elementId);
        if (!el) return null;
        try { return JSON.parse(el.textContent); }
        catch (e) { return null; }
    }

    initialize(ids) {
        this.state.config.settings = this._parseJson(ids.settingsId) || {};
        this.state.config.missions = this._parseJson(ids.missionsId) || [];
        this.state.config.rewards = this._parseJson(ids.rewardsId) || [];

        // Fallback
        if (this.state.config.rewards.length === 0) {
            this.state.config.rewards = [
                { id: 'reward_shipping', cost_stamps: 5, limit_per_user: 1 },
                { id: 'reward_product_100', cost_stamps: 10, limit_per_user: 1 },
                { id: 'reward_coupon_20k', cost_stamps: 15, limit_per_user: 1 },
                { id: 'reward_coupon_30k', cost_stamps: 30, limit_per_user: 1 }
            ];
        }

        this.state.config.gacha = this._parseJson(ids.gachaId) || {};

        const rawCustomerData = this._parseJson(ids.customerDataId);
        const eventHandle = ids.eventHandle;

        if (rawCustomerData && rawCustomerData.customer_id) {
            this.state.isLoggedIn = true;
            this.state.user.id = rawCustomerData.customer_id;

            const eventHistory = rawCustomerData.events && rawCustomerData.events[eventHandle];
            if (eventHistory) {
                this.state.user.currentStamps = eventHistory.current_stamps || 0;
                this.state.user.claimedRewards = eventHistory.rewards?.claimed_ids || [];

                if (eventHistory.mission_progress) {
                    const missionProgress = eventHistory.mission_progress;
                    const attendance = missionProgress.mission_attendance || missionProgress.attendance || {};
                    const order = missionProgress.mission_order || {};
                    const review = missionProgress.mission_review || {};
                    const bonusAll = missionProgress.all_missions_complete_bonus || missionProgress.mission_bonus_all || {};
                    const bonusOrder = missionProgress.mission_bonus_order || {};
                    const signup = missionProgress.mission_signup || {};

                    this.state.user.attendanceHistory = attendance.history || [];
                    this.state.user.missionCounts.attendance = Number(attendance.count || 0);
                    this.state.user.missionCounts.order = Number(order.count || 0);
                    this.state.user.missionCounts.review = Number(review.count || 0);
                    this.state.user.missionCounts.bonusAll = Number(bonusAll.count || 0);
                    this.state.user.missionCounts.bonusOrder = Number(bonusOrder.count || 0);
                    this.state.user.missionCounts.signup = Number(signup.count || 0);
                }

                const specialBonuses = eventHistory.special_bonuses || {};
                const luckyDropCount = (specialBonuses.count !== undefined)
                    ? Number(specialBonuses.count || 0)
                    : (Array.isArray(specialBonuses.list) ? specialBonuses.list.length : 0);
                this.state.user.missionCounts.luckyDrop = Number(luckyDropCount || 0);
            }
        }
    }

    get stamps() { return this.state.user.currentStamps; }
    getRewards() { return this.state.config.rewards; }
    getRewardById(rewardId) { return this.state.config.rewards.find(r => r.id == rewardId); }

    canRedeem(rewardId) {
        if (rewardId === 'reward_gacha') {
            const gachaCost = this.state.config.gacha ? (this.state.config.gacha.cost_stamps || 1) : 1;
            if (this.stamps < gachaCost) return { possible: false, reason: `코인이 부족합니다. (필요: ${gachaCost}, 보유: ${this.stamps})` };
            return { possible: true };
        }

        const reward = this.getRewardById(rewardId);
        if (!reward) return { possible: false, reason: "존재하지 않는 상품입니다." };
        if (this.stamps < reward.cost_stamps) return { possible: false, reason: `코인이 부족합니다. (필요: ${reward.cost_stamps}, 보유: ${this.stamps})` };
        if (this.state.user.claimedRewards.includes(String(rewardId))) return { possible: false, reason: "이미 교환한 선물입니다." };
        return { possible: true };
    }

    updateUserBalance(newBalance) { this.state.user.currentStamps = newBalance; }
    addClaimedReward(rewardId) {
        const idStr = String(rewardId);
        if (!this.state.user.claimedRewards.includes(idStr)) this.state.user.claimedRewards.push(idStr);
    }
    addAttendanceToday() {
        const today = new Date().toISOString().split('T')[0];
        if (!this.state.user.attendanceHistory.includes(today)) {
            this.state.user.attendanceHistory.push(today);
            this.state.user.missionCounts.attendance += 1;
        }
    }
}

/**
 * 3. Event Controller
 */
class EventController {
    constructor(config = {}) {
        this.store = new EventStateManager();
        this.api = null;
        this.config = {
            eventHandle: '2026-4th-event',
            settingsId: 'liquid-settings-data',
            missionsId: 'liquid-missions-data',
            rewardsId: 'liquid-rewards-data',
            gachaId: 'liquid-gacha-data',
            customerDataId: 'liquid-customer-data',
            ...config
        };
    }

    init() {
        this.store.initialize(this.config);
        if (this.store.state.isLoggedIn) {
            this.api = new EventApiClient(this.store.state.user.id, this.config.eventHandle);
        }
    }

    async tryCheckIn() {
        if (!this.api) return alert("로그인이 필요합니다.");
        try {
            const res = await this.api.checkIn();
            const newBalance = (res.new_balance !== undefined) ? res.new_balance : res.current_stamps;
            if (res.success || newBalance !== undefined) {
                if (newBalance !== undefined) this.store.updateUserBalance(newBalance);
                this.store.addAttendanceToday();
                return true;
            } else {
                alert("이미 출석했습니다.");
                return false;
            }
        } catch (e) {
            alert("출석체크 중 오류가 발생했습니다.");
            throw e;
        }
    }

    async tryExchange(rewardId, isGacha = false) {
        if (!this.api) { alert("로그인이 필요합니다."); return false; }

        const check = this.store.canRedeem(rewardId);
        if (!check.possible) { alert(check.reason); return false; }

        try {
            const res = await this.api.exchangeReward(rewardId);
            const resultPayload = (res && res.result && typeof res.result === "object") ? res.result : {};
            const isSuccess = (res && res.success === true) || (resultPayload && resultPayload.success === true);

            if (isSuccess) {
                if (!isGacha) {
                    this.store.addClaimedReward(rewardId);
                }

                if (isGacha) {
                    return { success: true, data: resultPayload };
                } else {
                    const modal = document.querySelector('.redeem_popup[data-popup="redeem_result"]');
                    const titleEl = modal?.querySelector('[data-role="title"]');
                    const descEl = modal?.querySelector('[data-role="desc"]');
                    const reward = this.store.getRewardById(rewardId);
                    const cost = reward ? reward.cost_stamps : 0;
                    const rewardTitle = resultPayload.title || (res && res.title) || "선물";

                    if (titleEl) titleEl.innerText = `${rewardTitle} 발급 완료`;
                    if (descEl) descEl.innerHTML = `코인 ${cost}개로 ${rewardTitle} 교환을<br>완료하였습니다`;
                    if (modal) $(modal).show();
                    return true;
                }
            } else {
                alert("교환에 실패했습니다.");
                return false;
            }
        } catch (e) {
            alert("일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요.");
            throw e;
        }
    }
}

/**
 * 4. Main Page Logic with jQuery UI Integration
 */
$(function () {
    // ✅ gsap/ScrollTrigger 로드 확인 + 등록을 맨 위로
    if (!window.gsap || !window.ScrollTrigger) {
        console.warn("GSAP 또는 ScrollTrigger가 로드되지 않았습니다.");
        return;
    }

    // 1. Controller Init
    const eventHandle = window.IPX_EVENT_HANDLE || '2026-4th-event';
    const controller = new EventController({ eventHandle: eventHandle });
    controller.init();
    const isEnPage = $(".january_event_container.en_ver").length > 0;
    const uiText = isEnPage
        ? {
            mission1Done: "Completed Today",
            mission1Default: "Check In Now",
            mission2Done: (max) => `Order ${max} Times Completed`,
            mission2Default: "Shop Now",
            mission3Done: (max) => `${max} Reviews Completed`,
            mission3Default: "Write a Review",
            bonusAll: { disabled: "Requires Missions 1-3", ready: "Claim Bonus Coins", done: "Claimed" },
            bonusOrder: { disabled: "Requires Mission 2", ready: "Shop Now", done: "Claimed" },
            loginRequired: "Log in to participate",
            processing: "Processing...",
        }
        : {
            mission1Done: "오늘 완료",
            mission1Default: "출석하고 코인 받기",
            mission2Done: (max) => `주문 ${max}회 달성 완료`,
            mission2Default: "바로 주문하러 가기",
            mission3Done: (max) => `리뷰 ${max}회 작성 완료`,
            mission3Default: "리뷰 쓰기",
            bonusAll: { disabled: "미션 1,2,3 완료시 가능", ready: "보너스 코인 받기", done: "획득 완료" },
            bonusOrder: { disabled: "미션 2. 완료시 가능", ready: "바로 주문하러 가기", done: "획득 완료" },
            loginRequired: "로그인 시 참여 가능",
            processing: "처리중...",
        };

    // =========================================================
    // ✅ UI Helper Functions (jQuery Based)
    // =========================================================

    function updateEtcButtonsDim() {
        const $items = $(".sc_gift .etc_list li[data-state]");
        if (!$items.length) return;

        $items.each(function () {
            const $item = $(this);
            const state = $item.data("state");
            const $btn = $item.find(".btn_wrap button");
            const $icon = $btn.find("img");

            if (state === "done" || state === "soldout" || state === "disabled") {
                $icon.attr("src", "https://vos.line-scdn.net/ipx-mall/images/out/showcase/260701/img/pc/coin_mini_dimmed.png");
                $btn.prop("disabled", true);
            } else {
                $icon.attr("src", "https://vos.line-scdn.net/ipx-mall/images/out/showcase/260701/img/pc/coin_mini.png");
                $btn.prop("disabled", false);
            }
        });
    }

    function updateGachaBtnDim() {
        const $wrap = $(".sc_gift .gacha_btn");
        if (!$wrap.length) return;

        $wrap.each(function () {
            const $btnWrap = $(this);
            const state = $btnWrap.attr("data-state");
            const $btn = $btnWrap.find("button");
            const $icon = $btn.find("img");
            const $text = $btn.find("span");

            if (state === "soldout" || state === "disabled") {
                $icon.attr("src", "https://vos.line-scdn.net/ipx-mall/images/out/showcase/260701/img/pc/coin_mini_dimmed.png");
                $btn.prop("disabled", true);
                $text.css("color", "#A0A0A0");
            } else {
                $icon.attr("src", "https://vos.line-scdn.net/ipx-mall/images/out/showcase/260701/img/pc/coin_mini.png");
                $btn.prop("disabled", false);
                $text.css("color", "");
            }
        });
    }

    function updateResponsiveImages() {
        const BREAKPOINT = 990;
        const isMobile = window.innerWidth <= BREAKPOINT;

        $(".attendance_days .day_item img").each(function () {
            const $img = $(this);
            const src = $img.attr("src");
            if (!src) return;
            if (isMobile && src.includes("/pc/")) $img.attr("src", src.replace("/pc/", "/mo/"));
            if (!isMobile && src.includes("/mo/")) $img.attr("src", src.replace("/mo/", "/pc/"));
        });

        $(".gacha_popup .result_img img").each(function () {
            const $img = $(this);
            const src = $img.attr("src");
            if (!src) return;
            if (isMobile && src.includes("/pc/")) $img.attr("src", src.replace("/pc/", "/mo/"));
            if (!isMobile && src.includes("/mo/")) $img.attr("src", src.replace("/mo/", "/pc/"));
        });
    }

    function updateMissionBar(missionKey, count, maxCount) {
        const $li = $(`.mission_content li[data-mission="${missionKey}"]`);
        if (!$li.length) return;
        const $cells = $li.find(".mission_bar_track span");
        $cells.removeClass("is-fill");
        $cells.each(function (idx) {
            if (idx < Math.min(count, maxCount)) $(this).addClass("is-fill");
        });
    }

    function getMissionMaxCount(missionId, fallback) {
        const missions = controller.store.state.config.missions || [];
        const mission = missions.find((item) => String(item.id) === String(missionId));
        return Number((mission && mission.max_count) || fallback || 0);
    }

    function updateMissionUI() {
        const counts = controller.store.state.user.missionCounts || {};
        const attendanceHistory = controller.store.state.user.attendanceHistory || [];
        const today = new Date().toISOString().split("T")[0];
        const isTodayChecked = attendanceHistory.includes(today);
        const isLoggedIn = !!controller.store.state.isLoggedIn;

        const attendanceMax = getMissionMaxCount("mission_attendance", 10);
        const orderMax = getMissionMaxCount("mission_order", 3);
        const reviewMax = getMissionMaxCount("mission_review", 5);
        const bonusOrderMax = getMissionMaxCount("mission_bonus_order", 3);
        const signupMax = getMissionMaxCount("mission_signup", 1);

        const mission1Done = isTodayChecked || Number(counts.attendance || 0) >= attendanceMax;
        const mission2Done = Number(counts.order || 0) >= orderMax;
        const mission3Done = Number(counts.review || 0) >= reviewMax;

        const missionRules = [
            { key: "mission1_attendance", done: mission1Done, doneText: uiText.mission1Done, defaultText: uiText.mission1Default, max: attendanceMax, count: Number(counts.attendance || 0) },
            { key: "mission2_order", done: mission2Done, doneText: uiText.mission2Done(orderMax), defaultText: uiText.mission2Default, max: orderMax, count: Number(counts.order || 0) },
            { key: "mission3_review", done: mission3Done, doneText: uiText.mission3Done(reviewMax), defaultText: uiText.mission3Default, max: reviewMax, count: Number(counts.review || 0) }
        ];

        missionRules.forEach((rule) => {
            const li = document.querySelector(`.mission_content li[data-mission="${rule.key}"]`);
            if (!li) return;
            li.setAttribute("data-state", rule.done ? "done" : "default");

            const target = li.querySelector(".btn_wrap button, .btn_wrap a");
            if (target) target.textContent = rule.done ? rule.doneText : rule.defaultText;

            updateMissionBar(rule.key, rule.count, rule.max);
        });

        const bonusAllCount = Number(counts.bonusAll || 0);
        const bonusOrderCount = Number(counts.bonusOrder || 0);
        const signupCount = Number(counts.signup || 0);
        const luckyDropCount = Number(counts.luckyDrop || 0);
        const bonusAllState = bonusAllCount >= 1 ? "done" : ((mission1Done && mission2Done && mission3Done) ? "ready" : "disabled");
        const bonusOrderState = bonusOrderCount >= bonusOrderMax ? "done" : (mission2Done ? "ready" : "disabled");

        const bonusRules = [
            { selector: '.mission_bonus li[data-bonus="mission_bonus_all"]', state: bonusAllState, texts: uiText.bonusAll, max: 1, count: bonusAllCount },
            { selector: '.mission_bonus li[data-bonus="mission_bonus_order"]', state: bonusOrderState, texts: uiText.bonusOrder, max: bonusOrderMax, count: bonusOrderCount }
        ];

        bonusRules.forEach((rule) => {
            const li = document.querySelector(rule.selector);
            if (!li) return;
            li.setAttribute("data-state", rule.state);
            const button = li.querySelector(".btn_wrap button");
            if (button) button.textContent = rule.texts[rule.state];
            const $cells = $(li).find(".mission_bar_track span");
            $cells.removeClass("is-fill");
            $cells.each(function (idx) {
                if (idx < Math.min(rule.count, rule.max)) $(this).addClass("is-fill");
            });
        });

        const signupLi = document.querySelector('.mission_bonus li[data-bonus="mission_signup"]');
        if (signupLi) {
            const $cells = $(signupLi).find(".mission_bar_track span");
            $cells.removeClass("is-fill");
            $cells.each(function (idx) {
                if (idx < Math.min(signupCount, signupMax)) $(this).addClass("is-fill");
            });
        }

        const luckyDropLi = document.querySelector('.mission_bonus li[data-bonus="mission_lucky_drop"]');
        if (luckyDropLi) {
            const $cells = $(luckyDropLi).find(".mission_bar_track span");
            $cells.removeClass("is-fill");
            $cells.each(function (idx) {
                if (idx < Math.min(luckyDropCount, 1)) $(this).addClass("is-fill");
            });
        }

        if (!isLoggedIn) {
            $(".mission_content .btn_wrap button, .mission_content .btn_wrap a").each(function () {
                this.textContent = uiText.loginRequired;
            });
        }
    }

    function applyAttendanceHistoryToCalendar() {
        const history = controller.store.state.user.attendanceHistory || [];
        const count = history.length;
        const $countEl = $(".attendance_popup .top_txt_box .txt_wrap .days");
        // if ($countEl.length) $countEl.text(`${count}일`);
        if ($countEl.length) isEnPage ? $countEl.text(`${count}days`) : $countEl.text(`${count}일`);

        $(".attendance_days .day_item").each(function () {
            const $item = $(this);
            const classList = ($item.attr("class") || "").split(/\s+/);
            const dayClass = classList.find((v) => /^day_\d+$/.test(v));
            if (!dayClass) return;
            const dayNum = dayClass.replace("day_", "").padStart(2, "0");
            const isDone = history.some((dateText) => typeof dateText === "string" && dateText.includes(`-07-${dayNum}`));
            $item.attr("data-state", isDone ? "done" : "locked");

            const $img = $item.find(".day_img img");
            if (!$img.length) return;
            const src = $img.attr("src") || "";
            if (isDone && !src.includes("_on.png")) {
                $img.attr("src", src.replace(".png", "_on.png"));
            } else if (!isDone && src.includes("_on.png")) {
                $img.attr("src", src.replace("_on.png", ".png"));
            }
        });
    }

    function syncUserStateFromPayload(payload) {
        if (!payload || typeof payload !== "object") return;

        const eventStateCandidates = [
            payload.event_data,
            payload.event,
            payload.user_event,
            payload.result && payload.result.event,
            payload.result && payload.result.event_data,
            payload.events && payload.events[eventHandle]
        ];
        const eventState = eventStateCandidates.find((v) => v && typeof v === "object") || payload;

        const missionProgress = eventState.mission_progress || {};
        const attendance = missionProgress.mission_attendance || missionProgress.attendance || {};
        const order = missionProgress.mission_order || {};
        const review = missionProgress.mission_review || {};
        const bonusAll = missionProgress.all_missions_complete_bonus || missionProgress.mission_bonus_all || {};
        const bonusOrder = missionProgress.mission_bonus_order || {};
        const signup = missionProgress.mission_signup || {};
        const specialBonuses = eventState.special_bonuses || payload.special_bonuses || {};
        const claimed = eventState.rewards && eventState.rewards.claimed_ids;

        const nextBalanceCandidates = [
            payload.new_balance,
            payload.current_stamps,
            eventState.current_stamps
        ];
        const nextBalance = nextBalanceCandidates.find((v) => v !== undefined && v !== null);
        if (nextBalance !== undefined) {
            controller.store.updateUserBalance(Number(nextBalance) || 0);
        }

        if (Array.isArray(claimed)) {
            controller.store.state.user.claimedRewards = claimed.map((v) => String(v));
        }

        if (Array.isArray(attendance.history)) {
            controller.store.state.user.attendanceHistory = attendance.history.slice();
        }
        if (attendance.count !== undefined) controller.store.state.user.missionCounts.attendance = Number(attendance.count || 0);
        if (order.count !== undefined) controller.store.state.user.missionCounts.order = Number(order.count || 0);
        if (review.count !== undefined) controller.store.state.user.missionCounts.review = Number(review.count || 0);
        if (bonusAll.count !== undefined) controller.store.state.user.missionCounts.bonusAll = Number(bonusAll.count || 0);
        if (bonusOrder.count !== undefined) controller.store.state.user.missionCounts.bonusOrder = Number(bonusOrder.count || 0);
        if (signup.count !== undefined) controller.store.state.user.missionCounts.signup = Number(signup.count || 0);
        if (specialBonuses.count !== undefined) {
            controller.store.state.user.missionCounts.luckyDrop = Number(specialBonuses.count || 0);
        } else if (Array.isArray(specialBonuses.list)) {
            controller.store.state.user.missionCounts.luckyDrop = specialBonuses.list.length;
        }
    }

    const updateButtonsUI = () => {
        const currentStamps = controller.store.stamps || 0;
        const claimedRewardIds = (controller.store.state.user.claimedRewards || []).map(String);
        const allRewards = controller.store.getRewards();
        const gachaConfig = controller.store.state.config.gacha;

        const coinEl = document.getElementById('display-coin-count');
        if (coinEl) coinEl.innerText = isEnPage ? `${currentStamps}` : `${currentStamps}개`;

        document.querySelectorAll('.coin_count').forEach(el => {
            el.innerText = currentStamps;
        });

        allRewards.forEach(reward => {
            const button = document.querySelector(`.btn_exchange[data-reward-id="${reward.id}"]`);
            if (!button) return;

            const li = button.closest('li');
            let newState = 'disabled';
            let newHtml = button.innerHTML;

            if (claimedRewardIds.includes(String(reward.id))) {
                newState = 'done';
                newHtml = '<span>교환 완료</span>';
            }
            else if (currentStamps >= reward.cost_stamps) {
                newState = 'default';
                if(!button.querySelector('img')) {
                    newHtml = `<img src="https://vos.line-scdn.net/ipx-mall/images/out/showcase/260701/img/pc/coin_mini.png" alt=""> <span>✕ ${reward.cost_stamps}개로 교환하기</span>`;
                }
            }
            else {
                newState = 'disabled';
                if (!button.querySelector('img')) {
                    newHtml = `<img src="https://vos.line-scdn.net/ipx-mall/images/out/showcase/260701/img/pc/coin_mini.png" alt=""> <span>✕ ${reward.cost_stamps}개로 교환하기</span>`;
                }
            }

            if (li) li.setAttribute('data-state', newState);
            button.innerHTML = newHtml;
        });

        const gachaBtn = document.querySelector('[data-action="gacha"]');
        if (gachaBtn) {
            const gachaCost = gachaConfig ? (gachaConfig.cost_stamps || 1) : 1;
            const canGacha = currentStamps >= gachaCost;

            const gachaWrap = gachaBtn.closest('.gacha_btn');
            if (gachaWrap) {
                gachaWrap.setAttribute('data-state', canGacha ? 'default' : 'disabled');
            }
        }

        updateEtcButtonsDim();
        updateGachaBtnDim();
        updateMissionUI();
        applyAttendanceHistoryToCalendar();
    };

    const updateAttendanceUI = () => {
        applyAttendanceHistoryToCalendar();
        updateMissionUI();
        const confirmBtn = document.querySelector('[data-action="confirm_attendance"]');
        if (confirmBtn) {
            confirmBtn.setAttribute('data-state', 'done_today');
            confirmBtn.innerText = '오늘 출석 완료';
            confirmBtn.disabled = true;
        }
    };

    const fetchLatestStatus = async () => {
        if (!controller.api) {
            const coinEl = document.getElementById('display-coin-count');
            if(coinEl) coinEl.innerText = "0개";

            document.querySelectorAll('.coin_count').forEach(el => {
                el.innerText = "0";
            });

            updateButtonsUI();
            return;
        }

        try {
            const response = await controller.api.getHistory(1);
            syncUserStateFromPayload(response);
            updateButtonsUI();
        } catch (error) {
            console.error("[API] 스탬프 동기화 실패:", error);
        }
    };

    const renderHistory = (historyList) => {
        const tbody = document.querySelector('.history_table--body tbody');
        if (!tbody) return;
        tbody.querySelectorAll('.history_row').forEach(row => row.remove());
        const emptyMsg = tbody.querySelector('.history_empty');

        if (!historyList || historyList.length === 0) {
            if(emptyMsg) emptyMsg.style.display = 'table-row';
        } else {
            if(emptyMsg) emptyMsg.style.display = 'none';
            const baseUrl = "https://vos.line-scdn.net/ipx-mall/images/out/showcase/260701/img/pc";

            historyList.forEach(item => {
                const tr = document.createElement('tr');
                tr.className = 'history_row';
                const date = item.timestamp ? item.timestamp.split('T')[0] : (item.created_at ? item.created_at.split('T')[0] : '-');
                const absAmount = Math.abs(item.amount);
                let coinDisplay = `${absAmount}`;
                let badgeType = 'type_exchange';
                let badgeText = '사용';
                let imgSrc = `${baseUrl}/gift_box.png`;

                if (item.type === 'gacha_redeem') {
                    badgeType = 'type_gacha'; badgeText = '가챠'; imgSrc = `${baseUrl}/lucky_ball.png`;
                } else if (item.type === 'redeem') {
                    badgeType = 'type_exchange'; badgeText = '교환'; imgSrc = `${baseUrl}/gift_box.png`;
                } else if (item.amount > 0) {
                    badgeType = 'type_gacha'; badgeText = '적립'; imgSrc = `${baseUrl}/coin_mini.png`; coinDisplay = `+${absAmount}`;
                }

                tr.innerHTML = `
                    <td class="td_info">
                        <div class="item_img"><img src="${imgSrc}" alt="" /></div>
                        <div class="item_text">
                            <span class="item_badge ${badgeType}">${badgeText}</span>
                            <span class="item_name">${item.description || item.title || '이벤트 참여'}</span>
                        </div>
                    </td>
                    <td class="td_date">${date}</td>
                    <td class="td_coin">${coinDisplay}</td>
                `;
                tbody.appendChild(tr);
            });
        }
        if (window.updateCoinPopupScroll) window.updateCoinPopupScroll();
    };

    // =========================================================
    // ✅ Event Listeners & Interaction Init
    // =========================================================

    // 1. 출석체크 로직
    const confirmAttendanceBtn = document.querySelector('[data-action="confirm_attendance"]');
    if (confirmAttendanceBtn) {
        confirmAttendanceBtn.addEventListener('click', async (e) => {
            e.preventDefault();

            if (confirmAttendanceBtn.disabled) return;

            const originalText = confirmAttendanceBtn.innerText;

            confirmAttendanceBtn.disabled = true;
            confirmAttendanceBtn.innerText = uiText.processing;

            try {
                const isSuccess = await controller.tryCheckIn();

                if (isSuccess) {
                    await fetchLatestStatus();
                    updateAttendanceUI();
                } else {
                    confirmAttendanceBtn.disabled = false;
                    confirmAttendanceBtn.innerText = originalText;
                }
            } catch (err) {
                console.error(err);
                confirmAttendanceBtn.disabled = false;
                confirmAttendanceBtn.innerText = originalText;
                alert("일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요.");
            }
        });
    }

    // 2. 일반 선물 교환 버튼
    document.querySelectorAll('.btn_exchange').forEach(btn => {
        btn.addEventListener('click', async (e) => {
            e.preventDefault();
            const targetBtn = e.currentTarget;
            const li = targetBtn.closest('li');
            const rewardId = targetBtn.dataset.rewardId;

            if (!rewardId) return;

            if (li && (li.getAttribute('data-state') === 'disabled' || li.getAttribute('data-state') === 'done')) {
                return;
            }

            const originalState = li ? li.getAttribute('data-state') : 'default';
            const originalHtml = targetBtn.innerHTML;

            if (li) li.setAttribute('data-state', 'disabled');
            targetBtn.innerHTML = `<span>${uiText.processing}</span>`;

            updateEtcButtonsDim();

            try {
                const isSuccess = await controller.tryExchange(rewardId, false);

                if (isSuccess) {
                    await fetchLatestStatus();
                } else {
                    targetBtn.innerHTML = originalHtml;
                    if (li) li.setAttribute('data-state', originalState);
                    updateEtcButtonsDim();
                }
            } catch (err) {
                console.error("Exchange Error:", err);
                targetBtn.innerHTML = originalHtml;
                if (li) li.setAttribute('data-state', originalState);
                updateEtcButtonsDim();
            }
        });
    });

    // 3. 코인 히스토리 팝업 + 조회
    const $coinPopup = $('.coin_popup[data-popup="coin_popup"]');
    const $coinOpenButtons = $('.coin_popup_open');
    const $coinCloseButton = $coinPopup.find('.popup_close');
    const $coinScrollArea = $coinPopup.find('.history_scroll');
    const $coinRail = $coinPopup.find('.scroll_rail');
    const $coinThumb = $coinPopup.find('.scroll_thumb');

    const updateCoinPopupScroll = () => {
        if (!$coinScrollArea.length || !$coinRail.length || !$coinThumb.length) return;

        const scrollEl = $coinScrollArea[0];
        const railEl = $coinRail[0];
        const thumbEl = $coinThumb[0];
        const viewHeight = scrollEl.clientHeight;
        const contentHeight = scrollEl.scrollHeight;

        if (contentHeight <= viewHeight + 1) {
            $coinRail.hide();
            return;
        }

        $coinRail.show();

        const railHeight = railEl.clientHeight;
        const thumbHeight = Math.max(36, (viewHeight / contentHeight) * railHeight);
        $coinThumb.height(thumbHeight);

        const maxScroll = contentHeight - viewHeight;
        const maxTop = railHeight - thumbHeight;
        const ratio = maxScroll > 0 ? (scrollEl.scrollTop / maxScroll) : 0;
        thumbEl.style.top = `${maxTop * ratio}px`;
    };

    window.updateCoinPopupScroll = updateCoinPopupScroll;

    if ($coinScrollArea.length) {
        $coinScrollArea.on('scroll', updateCoinPopupScroll);
        $(window).on('resize', updateCoinPopupScroll);
    }

    const openCoinPopup = () => {
        if (!$coinPopup.length) return;
        $coinPopup.show();
        $("body").addClass("is-popup-open");
        requestAnimationFrame(updateCoinPopupScroll);
    };

    const closeCoinPopup = () => {
        if (!$coinPopup.length) return;
        $coinPopup.hide();
        $("body").removeClass("is-popup-open");
    };

    if ($coinPopup.length) {
        $coinOpenButtons.on('click', async (e) => {
            e.preventDefault();
            if (!controller.api) {
                alert('로그인이 필요합니다.');
                return;
            }

            try {
                const res = await controller.api.getHistory(50);
                renderHistory(res.history || []);
            } catch (err) {
                console.error('히스토리 로드 실패:', err);
            }

            openCoinPopup();
        });

        $coinCloseButton.on('click', (e) => {
            e.preventDefault();
            closeCoinPopup();
        });

        $coinPopup.on('click', '.dimmed_wrap', (e) => {
            e.preventDefault();
            closeCoinPopup();
        });

        $(document).on('keydown', (e) => {
            if (e.key === 'Escape' && $coinPopup.is(':visible')) {
                closeCoinPopup();
            }
        });
    }

    // [Init] 반응형 이미지 리사이즈 리스너 등록
    updateResponsiveImages();
    $(window).on("resize", updateResponsiveImages);

    // [Popup] 캘린더(출석) 팝업 제어
    const $attendancePopup = $('.attendance_popup[data-popup="attendance_popup"]');
    if ($attendancePopup.length) {
        const $openBtn = $(".sc_mission .attendance_btn button");
        const $closeBtn = $attendancePopup.find(".popup_close");

        const openAttendancePopup = () => { $attendancePopup.show(); $("body").addClass("is-popup-open"); };
        const closeAttendancePopup = () => { $attendancePopup.hide(); $("body").removeClass("is-popup-open"); };

        $openBtn.on("click", (e) => { e.preventDefault(); openAttendancePopup(); });
        $closeBtn.on("click", (e) => { e.preventDefault(); closeAttendancePopup(); });
        $attendancePopup.on("click", ".dimmed_wrap.mo_only", (e) => { e.preventDefault(); closeAttendancePopup(); });
    }

    // [Mission] 주문하기 버튼 클릭 시 상품 페이지로 이동
    $(document).on("click", ".mission_content li[data-mission='mission2_order'] .btn_wrap button", function(e) {
        const $button = $(this);
        const $li = $button.closest("li");
        const state = $li.attr("data-state");
        const buttonText = $button.text().trim();

        // 로그인 필요 상태이거나 done 상태면 이동하지 않음
        if (buttonText === uiText.loginRequired || state === "done") {
            e.preventDefault();
            return;
        }

        // 기본 상태일 때만 이동
        if (state === "default" || !state) {
            e.preventDefault();
            window.location.href = isEnPage ? "/en/collections/all" : "/collections/all";
        }
    });

    // [Mission Bonus] 보너스 주문하기 버튼 클릭 시 상품 페이지로 이동
    $(document).on("click", ".mission_bonus li[data-bonus='mission_bonus_order'] .btn_wrap button", function(e) {
        const $button = $(this);
        const $li = $button.closest("li");
        const state = $li.attr("data-state");
        const buttonText = $button.text().trim();

        // disabled 또는 done 상태면 이동하지 않음
        if (state === "disabled" || state === "done") {
            e.preventDefault();
            return;
        }

        // ready 상태일 때만 이동
        if (state === "ready") {
            e.preventDefault();
            window.location.href = isEnPage ? "/en/collections/all" : "/collections/all";
        }
    });

    // =========================================================
    // ✅ Gacha: 클릭 시 data-state 변경 후 '즉시 UI 반영' 추가
    // =========================================================
    (function initGachaFlow() {
        const $gachaPopup = $('.gacha_popup[data-popup="gacha_result"]');
        const $motionWrap = $(".sc_gift .motion_wrap");
        if (!$motionWrap.length) return;

        const getGachaResultUiContent = (resultId, descText = "", typeText = "") => {
            const copyByResultId = isEnPage
                ? {
                    item_1st_gacha: {
                        resultRank: "1st",
                        badgeText: "1st Place",
                        imgFileName: "gacha_result_img_01.png",
                        titleHtml: `<span class="bold_span">BROWN X Mr Maria<br />STAR LIGHT (50cm) <br class="mo_only">Voucher</span>`,
                    },
                    item_2st_gacha: {
                        resultRank: "2nd",
                        badgeText: "2nd Place",
                        imgFileName: "gacha_result_img_02.png",
                        titleHtml: `<span class="bold_span">JOGUMAN X Bmix <br class="mo_only">WOODY<br class="pc_only">CERAMIC <br class="mo_only">CLOCK Voucher</span>`,
                    },
                    item_3st_gacha: {
                        resultRank: "3rd",
                        badgeText: "3rd Place",
                        imgFileName: "gacha_result_img_03.png",
                        titleHtml: `<span class="bold_span">POKEMON MINI <br class="mo_only">CAMERA<br class="pc_only">DITTO <br class="mo_only">EDITION Voucher</span>`,
                    },
                    item_4st_gacha: {
                        resultRank: "4th",
                        badgeText: "4th Place",
                        imgFileName: "gacha_result_img_05.png",
                        titleHtml: `<span class="bold_span">LINE FRIENDS SALLY <br class="mo_only">DAILY<br class="pc_only">SOFT HAND <br class="mo_only">TOWEL Voucher</span>`,
                    },
                    item_5st_gacha: {
                        resultRank: "5th",
                        badgeText: "5th Place",
                        imgFileName: "gacha_result_img_06.png",
                        titleHtml: `<span class="bold_span">3,000 KRW<br />Cart Coupon</span>`,
                    },
                    item_6st_gacha: {
                        resultRank: "lose",
                        badgeText: "Bonus",
                        imgFileName: "gacha_result_img_07.png",
                        titleHtml: `<span class="bold_span">1,000 KRW <br class="mo_only">Unlimited<br class="pc_only">Daily Cart Coupon</span>`,
                    },
                }
                : {
                    item_1st_gacha: {
                        resultRank: "1st",
                        badgeText: "1등",
                        imgFileName: "gacha_result_img_01.png",
                        titleHtml: `<span class="bold_span">BROWN X Mr Maria <br />STAR LIGHT (50cm) 교환권</span>`,
                    },
                    item_2st_gacha: {
                        resultRank: "2nd",
                        badgeText: "2등",
                        imgFileName: "gacha_result_img_02.png",
                        titleHtml: `<span class="bold_span">조구만 X 비믹스 우디 <br />오피스 세라믹 <br class="mo_only">시계 교환권</span>`,
                    },
                    item_3st_gacha: {
                        resultRank: "3rd",
                        badgeText: "3등",
                        imgFileName: "gacha_result_img_03.png",
                        titleHtml: `<span class="bold_span">스냅 스퀘어 포켓몬 <br />미니 카메라 메타몽<br class="mo_only"> 에디션 교환권</span>`,
                    },
                    item_4st_gacha: {
                        resultRank: "4th",
                        badgeText: "4등",
                        imgFileName: "gacha_result_img_05.png",
                        titleHtml: `<span class="bold_span">라인프렌즈 샐리 <br class="mo_only"> 데일리 <br class="pc_only">소프트 핸드타월  <br class="mo_only">교환권</span>`,
                    },
                    item_5st_gacha: {
                        resultRank: "5th",
                        badgeText: "5등",
                        imgFileName: "gacha_result_img_06.png",
                        titleHtml: `<span class="bold_span">장바구니 <br />3천 원 할인 쿠폰</span>`,
                    },
                    item_6st_gacha: {
                        resultRank: "lose",
                        badgeText: "보너스",
                        imgFileName: "gacha_result_img_07.png",
                        titleHtml: `<span>아쉬운 마음을 달래줄 <br><span class="bold_span">데일리 무제한 <br class="mo_only">1천 원 할인 쿠폰</span></span>`,
                    },
                };

            if (copyByResultId[resultId]) {
                return copyByResultId[resultId];
            }

            const merged = `${descText} ${typeText}`;
            const mergedLower = merged.toLowerCase();
            if (merged.includes("샐리") || mergedLower.includes("sally")) return copyByResultId.item_4st_gacha;
            if (merged.includes("3천") || mergedLower.includes("3,000") || mergedLower.includes("3000")) return copyByResultId.item_5st_gacha;
            if (merged.includes("1천") || merged.includes("무제한") || mergedLower.includes("1,000") || mergedLower.includes("1000") || mergedLower.includes("unlimited") || mergedLower.includes("miss")) {
                return copyByResultId.item_6st_gacha;
            }

            return copyByResultId.item_6st_gacha;
        };

        const LOTTIE_PATH =
            "https://vos.line-scdn.net/ipx-mall/images/out/showcase/251223/js/particles_01.json";
        let particlesAnim = null;

        function getLottieLib() {
            return window.lottie || window.bodymovin || null;
        }

        function ensureParticles() {
            const lottie = getLottieLib();
            if (!lottie) return null;
            if (!$gachaPopup.length) return null;

            const container = $gachaPopup.find('[data-role="gacha_particles"]')[0];
            if (!container) return null;

            if (particlesAnim) return particlesAnim;

            container.innerHTML = "";
            particlesAnim = lottie.loadAnimation({
                container,
                renderer: "svg",
                loop: false,
                autoplay: false,
                path: LOTTIE_PATH,
            });

            return particlesAnim;
        }

        function playParticles() {
            const anim = ensureParticles();
            if (!anim) return;
            anim.stop();
            anim.goToAndPlay(0, true);
        }

        ensureParticles();

        const openGachaPopup = () => {
            if (!$gachaPopup.length) return;

            $gachaPopup.show();

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    playParticles();
                });
            });
        };

        const closeGachaPopup = () => {
            if (!$gachaPopup.length) return;
            if (particlesAnim) particlesAnim.stop();
            $gachaPopup.hide();
        };

        $(document).on("click", ".gacha_popup [data-action='go_gift']", function (e) {
            const url = ($(this).attr("href") || "").trim();
            if (!url || url === "#") {
                e.preventDefault();
                return;
            }
            e.preventDefault();
            window.open(url, "_blank", "noopener,noreferrer");
        });

        $(document).on("click", ".sc_gift .gacha_btn button", async function (e) {
            e.preventDefault();
            const $thisBtn = $(this);
            const $btnWrap = $thisBtn.closest(".gacha_btn");
            const $textSpan = $thisBtn.find("span");

            const currentState = $btnWrap.attr("data-state");

            if (currentState === "soldout" || currentState === "disabled") return;

            const originalState = currentState || 'default';
            const originalHtml = $textSpan.html();

            $btnWrap.attr("data-state", "disabled");
            $textSpan.text(uiText.processing);

            if (typeof updateGachaBtnDim === 'function') {
                updateGachaBtnDim();
            }

            $motionWrap.addClass("is-playing");

            try {
                const gachaPromise = controller.tryExchange('reward_gacha', true);
                const delayPromise = new Promise(resolve => setTimeout(resolve, 5000));

                const [response, _] = await Promise.all([gachaPromise, delayPromise]);

                $motionWrap.removeClass("is-playing");

                $textSpan.html(originalHtml);

                if (response && response.success) {
                    const resultData = response.data;

                    const $popupTitle = $gachaPopup.find('[data-role="result_title"]');
                    const $popupBadge = $gachaPopup.find('[data-role="result_badge"]');
                    const $popupImg = $gachaPopup.find('[data-role="result_img"] img');
                    const $btnGift = $gachaPopup.find('[data-action="go_gift"]');
                    const $btnVoucher = $gachaPopup.find('.result_btns .btn_secondary');
                    const $btnCouponPage = $gachaPopup.find('[data-action="go_coupon"]');

                    const descriptionText = String(resultData.description || "");
                    const typeText = String(resultData.type || "");
                    const resultId = String(resultData.id || "");
                    const uiContent = getGachaResultUiContent(resultId, descriptionText, typeText);

                    const isMobile = window.innerWidth <= 990;
                    const imgPath = isMobile ? 'mo' : 'pc';
                    const baseUrl = `https://vos.line-scdn.net/ipx-mall/images/out/showcase/260701/img/${imgPath}`;
                    const titleHtml = uiContent.titleHtml;
                    const imgFileName = uiContent.imgFileName;
                    const badgeText = uiContent.badgeText;
                    const resultRank = uiContent.resultRank;

                    if ($popupTitle.length) $popupTitle.html(titleHtml);
                    $gachaPopup.attr("data-result", resultRank);

                    if ($popupImg.length) $popupImg.attr("src", `${baseUrl}/${imgFileName}`);
                    if ($popupBadge.length) $popupBadge.text(badgeText);

                    const isTopPrize = ["1st", "2nd", "3rd", "4th"].includes(resultRank);
                    const isCouponOnlyPrize = resultRank === "5th" || resultRank === "lose" || resultId === "item_6st_gacha" || resultData.type === "miss";

                    if (isTopPrize) {
                        const giftUrl = (resultData.reward_detail && resultData.reward_detail.reference_id)
                            ? String(resultData.reward_detail.reference_id).trim()
                            : "";
                        $btnGift.show();
                        $btnVoucher.show();
                        $btnCouponPage.hide();
                        $btnGift.attr('href', giftUrl || '#');
                        $btnGift.attr('target', '_blank');
                        $btnGift.attr('rel', 'noopener noreferrer');
                    } else if (isCouponOnlyPrize) {
                        $btnGift.hide();
                        $btnVoucher.hide();
                        if ($btnCouponPage.length) $btnCouponPage.show();
                    } else {
                        $btnGift.show();
                        $btnVoucher.show();
                        if ($btnCouponPage.length) $btnCouponPage.hide();
                        $btnGift.attr('href', (resultData.reward_detail && resultData.reward_detail.reference_id) ? resultData.reward_detail.reference_id : '#');
                    }

                    openGachaPopup();

                    await fetchLatestStatus();

                    // 가챠 성공 후 버튼 상태 재확인 및 UI 업데이트
                    if (typeof updateGachaBtnDim === 'function') updateGachaBtnDim();

                } else {
                    $btnWrap.attr("data-state", originalState);
                    if (typeof updateGachaBtnDim === 'function') updateGachaBtnDim();
                }

            } catch (err) {
                console.error("Gacha Failed:", err);
                $motionWrap.removeClass("is-playing");

                $btnWrap.attr("data-state", originalState);
                $textSpan.html(originalHtml);
                if (typeof updateGachaBtnDim === 'function') updateGachaBtnDim();

                alert("일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요.");
            }
        });

        $(document).on("click", ".gacha_popup .popup_close, .gacha_popup .dimmed_wrap", function (e) {
            e.preventDefault();
            closeGachaPopup();
        });
    })();

    // 초기 실행
    (async () => {
        await fetchLatestStatus();
    })();

    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") fetchLatestStatus();
    });
    window.addEventListener("focus", () => { fetchLatestStatus(); });

    // ✅ Sticky (중복이면 하나만 남겨)
    if (
        $(".sticky_wrap").length &&
        $(".sc_mission").length &&
        $(".sc_process").length
    ) {
        ScrollTrigger.create({
            trigger: ".sc_mission",
            start: "top top",
            endTrigger: ".sc_process",
            end: "bottom bottom",
            toggleClass: { targets: ".sticky_wrap", className: "type_fixed" },
        });
    }

    // =========================
    // ✅ 모바일 토글
    // =========================
    const $moLineWrap = $(".line_wrap.mo");
    const $moBtn = $moLineWrap.find(".line_btn");
    const $moPanel = $moLineWrap.find(".toggle_panel");

    if ($moLineWrap.length && $moBtn.length && $moPanel.length) {
        $moPanel.css("height", 0);
        let isOpen = false;

        function openMoPanel() {
            const h = $moPanel.prop("scrollHeight");

            $moBtn.attr("aria-expanded", "true");
            $moPanel.attr("aria-hidden", "false");

            $moLineWrap.addClass("is-open");

            gsap.to($moPanel, {
                height: h,
                duration: 0.2,
                ease: "power1.out",
                onComplete: () => $moPanel.css("height", "auto"),
            });

            isOpen = true;
        }

        function closeMoPanel() {
            const currentH = $moPanel.outerHeight();
            $moPanel.css("height", currentH);

            $moBtn.attr("aria-expanded", "false");
            $moPanel.attr("aria-hidden", "true");

            $moLineWrap.removeClass("is-open");

            gsap.to($moPanel, {
                height: 0,
                duration: 0.2,
                ease: "power1.in",
            });

            isOpen = false;
        }

        $moBtn.on("click", function () {
            isOpen ? closeMoPanel() : openMoPanel();
        });

        $moPanel.find("a").on("click", function () {
            if (isOpen) closeMoPanel();
        });
    }

    // =========================================================
    // ✅ 비로그인 상태 버튼 텍스트 변경 (sc_mission)
    // =========================================================
    if ($(".january_event_container[data-auth='out']").length) {
        $(".sc_mission .btn_wrap button").text(uiText.loginRequired);
    }

    // =========================================================
    // ✅ 비로그인 상태 버튼 비활성화 처리 (sc_gift)
    // =========================================================
    const $container = $(".january_event_container.ko_ver");

    if ($container.length && $container.data("auth") === "out") {
        $(".sc_gift .gacha_btn").attr("data-state", "disabled");
        $(".sc_gift .etc_list li[data-state]").attr("data-state", "disabled");
        $(".sc_gift .gacha_btn button, .sc_gift .etc_list .btn_wrap button").prop(
            "disabled",
            true,
        );
    }

    // =========================
    // ✅ redeem_popup 여닫기
    // =========================
    const $redeemPopup = $('.redeem_popup[data-popup="redeem_result"]');
    if ($redeemPopup.length) {
        $(document).on("click", ".redeem_popup .popup_close", function (e) {
            e.preventDefault();
            closeRedeemPopup();
        });

        $(document).on("click", ".redeem_popup .dimmed_wrap", function (e) {
            e.preventDefault();
            closeRedeemPopup();
        });

        $(document).on(
            "click",
            '.redeem_popup [data-action="close"]',
            function (e) {
                e.preventDefault();
                closeRedeemPopup();
            },
        );

        function closeRedeemPopup() {
            $redeemPopup.hide();
        }
    }

    // 스와이퍼
    const swiper = new Swiper(".mySwiper", {
        slidesPerView: 1,
        loop: true,

        effect: "fade",
        fadeEffect: { crossFade: true },

        autoHeight: true,

        pagination: {
            el: ".process-pagination",
            clickable: true,
        },
    });

    const swiperCommonOptions = {
        slidesPerView: "auto",
        slidesPerGroup: 1,
        spaceBetween: 12,
        speed: 400,
        autoplay: false,
        centeredSlides: false,
        initialSlide: 0,

        slidesOffsetBefore: 16,
        slidesOffsetAfter: 16,

        grabCursor: true,
        touchRatio: 1,
        resistanceRatio: 0.85,

        breakpoints: {
            991: {
                slidesPerView: 5,
                slidesPerGroup: 1,
                spaceBetween: 20,
                slidesOffsetBefore: 0,
                slidesOffsetAfter: 0,
            },
        },
    };

    const mySwiper1 = new Swiper(".sc_shop .mySwiper1", {
        ...swiperCommonOptions,

        navigation: {
            prevEl: ".sc_shop .top_content .swiper-button-prev",
            nextEl: ".sc_shop .top_content .swiper-button-next",
        },
    });

    const mySwiper2 = new Swiper(".sc_shop .mySwiper2", {
        ...swiperCommonOptions,

        navigation: {
            prevEl: ".sc_shop .center_content .swiper-button-prev",
            nextEl: ".sc_shop .center_content .swiper-button-next",
        },
    });

    const mySwiper3 = new Swiper(".sc_shop .mySwiper3", {
        ...swiperCommonOptions,

        navigation: {
            prevEl: ".sc_shop .bottom_content .swiper-button-prev",
            nextEl: ".sc_shop .bottom_content .swiper-button-next",
        },
    });

    // sc_lucky
    const luckyList = document.querySelector(".sc_lucky .content_wrap ul");

    if (luckyList) {
        const luckyObserver = new IntersectionObserver(
            ([entry], observer) => {
                if (entry.isIntersecting) {
                    luckyList.classList.add("is_active");
                    observer.unobserve(entry.target);
                }
            },
            {
                threshold: 0.25,
            },
        );

        luckyObserver.observe(luckyList);
    }

    /// 모션
    gsap.registerPlugin(ScrollTrigger);

    // sc_main 모션
    const mainIntroTl = gsap.timeline({
        scrollTrigger: {
            trigger: ".sc_main",
            start: "top 70%",
            once: true,
        },
        defaults: {
            ease: "power2.out",
        },
    });

    mainIntroTl.to(".sc_main .main_title_wrap", {
        opacity: 1,
        y: 0,
        duration: 0.6,
    });

    mainIntroTl.to(
        ".sc_main .main_txt_wrap p",
        {
            opacity: 1,
            y: 0,
            duration: 0.45,
            stagger: 0.12,
        },
        "-=0.15",
    );

    mainIntroTl.to(
        ".sc_main .img_box img",
        {
            opacity: 1,
            duration: 0.6,
        },
        "-=0.4",
    );

    // sc_coin 모션
    const coinIntroTl = gsap.timeline({
        scrollTrigger: {
            trigger: ".sc_coin",
            start: "top 70%",
            once: true,
        },
        defaults: { ease: "power2.out" },
    });

    coinIntroTl.to(
        [
            ".sc_coin .title_wrap",
            ".sc_coin .txt_wrap",
            ".sc_coin .status_wrap .flex_wrap",
        ],
        {
            opacity: 1,
            y: 0,
            duration: 0.6,
        },
    );

    coinIntroTl.to(
        ".sc_coin .status_wrap",
        {
            opacity: 1,
            duration: 0.01,
        },
        0,
    );

    //sc_mission 모션
    const missionIntroTl = gsap.timeline({
        scrollTrigger: {
            trigger: ".sc_mission",
            start: "top 70%",
            once: true,
        },
        defaults: { ease: "power2.out" },
    });

    missionIntroTl.to(
        [
            ".sc_mission .title_wrap",
            ".sc_mission .txt_wrap",
            ".sc_mission .mission_content",
        ],
        {
            opacity: 1,
            y: 0,
            duration: 0.6,
        },
    );

    //sc_gift 모션
    const giftIntroTl = gsap.timeline({
        scrollTrigger: {
            trigger: ".sc_gift",
            start: "top 70%",
            once: true,
        },
        defaults: { ease: "power2.out" },
    });

    giftIntroTl.to(
        [
            ".sc_gift .title_wrap",
            ".sc_gift .txt_wrap",
            ".sc_gift .gift_content",
            ".sc_gift .etc_txt",
        ],
        {
            opacity: 1,
            y: 0,
            duration: 0.6,
        },
    );

    // 출석체크 캘린더
    $(".attendance_days .day_item").each(function () {
        const $item = $(this);
        const state = $item.attr("data-state");

        if (state !== "done") return;

        const $dayImg = $item.find(".day_img img");
        const imgSrc = $dayImg.attr("src");

        const activeSrc = imgSrc.replace(".png", "_on.png");

        $dayImg.attr("src", activeSrc);
    });

    // 모바일 sc_main
    // sc_main anchor
     var luckyCoinTxt = document.querySelector(".sc_main .main_txt_01");
  var luckyShopTxt = document.querySelector(".sc_main .main_txt_02");
  var luckyCoinSection = document.querySelector(".sc_coin");
  var luckyShopSection = document.querySelector(".sc_shop");

  function isMobile() {
    return window.innerWidth <= 990;
  }

  function smoothScrollTo(target) {
    if (!target) return;

    target.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }

  // 모바일 기본 상태: main_txt_01 hover 켜기
  function setMainTxtDefaultActive() {
    if (!isMobile()) return;

    if (luckyCoinTxt) luckyCoinTxt.classList.add("is_active");
    if (luckyShopTxt) luckyShopTxt.classList.remove("is_active");
  }

  setMainTxtDefaultActive();

  window.addEventListener("resize", function () {
    setMainTxtDefaultActive();
  });

  if (luckyCoinTxt) {
    luckyCoinTxt.addEventListener("click", function () {
      if (isMobile()) {
        luckyCoinTxt.classList.add("is_active");
        if (luckyShopTxt) luckyShopTxt.classList.remove("is_active");
      }

      smoothScrollTo(luckyCoinSection);
    });
  }

  if (luckyShopTxt) {
    luckyShopTxt.addEventListener("click", function () {
      if (isMobile()) {
        // 02 누르면 01은 default 상태로
        if (luckyCoinTxt) luckyCoinTxt.classList.remove("is_active");

        // 02는 hover 상태로
        luckyShopTxt.classList.add("is_active");

        setTimeout(function () {
          smoothScrollTo(luckyShopSection);

          // setTimeout(function () {
          //   luckyShopTxt.classList.remove("is_active");

          //   // 스크롤 후 다시 01 hover 상태로 돌아가게 하고 싶으면 유지
          //   if (luckyCoinTxt) luckyCoinTxt.classList.add("is_active");
          // }, 700);
        }, 500);
      } else {
        smoothScrollTo(luckyShopSection);
      }
    });
  }

    // QA 후 주석 해제
//   // sc_shop, sc_lucky, main_txt_02 노출 기간 제어
// var periodShopSection = document.querySelector(".sc_shop");
// var periodLuckySection = document.querySelector(".sc_lucky");
// var periodShopMainBtn = document.querySelector(".sc_main .main_txt_02");

// // 2026년 7월 9일 오전 11시 ~ 2026년 7월 16일 오후 6시
// var eventStartDate = new Date("2026/07/09 11:00:00");
// var eventEndDate = new Date("2026/07/16 18:00:00");

// function setPeriodSectionVisible() {
//   var nowDate = new Date();
//   var isEventPeriod = nowDate >= eventStartDate && nowDate <= eventEndDate;

//   if (periodShopSection) {
//     periodShopSection.classList.toggle("is_period_hidden", !isEventPeriod);
//   }

//   if (periodLuckySection) {
//     periodLuckySection.classList.toggle("is_period_hidden", !isEventPeriod);
//   }

//   if (periodShopMainBtn) {
//     periodShopMainBtn.classList.toggle("is_period_hidden", !isEventPeriod);
//   }
// }

// setPeriodSectionVisible();

});
